import moment from 'moment';
import { TimeRange } from './enums';
import { TimeEntity } from '../dtos/reports-data-response';
import { DateOption } from '../components/shared/date-range-picker/date-range-picker';

export const COMMON_FORMAT = 'M/DD/YYYY';
export const NTH_DAY_OF_MONTH_FORMAT = 'MMMM Do YYYY';
export const WORDS_MONTHS_FORMAT = 'MMM DD YYYY';
export const WORDS_MONTHS_HOURS_FORMAT = 'MMM DD YYYY HH:mm';
export const WORDS_MONTHS_SHORT_FORMAT = 'MMM DD, YYYY';
export const SHORT_MONTH_DATE_FORMAT = 'MM/DD/YYYY HH:mm';
export const LOGS_FORMAT = 'MMDDYYYY_HHmmss';
export const TIMESTAMP_DATE_FORMAT = 'MM/DD/yyyy HH:mm:ss';
export const SHORT_MONTH_DAY_YEAR_FORMAT = 'MMM D YYYY';
export const COMMON_DATE_TIME_FORMAT = 'M/DD/YYYY HH:mm';

export interface DateRangeObject {
    from: Date;
    to: Date;
}

export type DateOptionKey = keyof typeof DATE_OPTIONS_MAP;

export type DateRange = DateOptionKey | DateRangeObject;

export const DATE_OPTIONS_MAP: Record<string, DateOption> = {
    'Last 24 hours': { value: 24, unit: 'hours' },
    'Last 7 days': { value: 7, unit: 'days' },
    'Last 30 days': { value: 30, unit: 'days'},
};

export const HOUR_FORMAT = 'HH:mm';

export const dateFormat = (date: Date = new Date(), format = '') => convertDateFormat(date, format);
export const convertDateFormat = (date: string | Date, format = '') => {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format(format || COMMON_FORMAT) : '';
};

export const lastWeekRang = () => `${insightsDateFormat(subtractDaysFromToday(7))} - ${insightsDateFormat(subtractDaysFromToday(1))}`;
export const subtractDaysFromToday = (daysNumber: number) => subtractDaysByDate(daysNumber, new Date());
export const subtractDaysByDate = (daysNumber: number, date: Date | '') => moment(date).subtract(daysNumber, 'days').toDate();
export const addDaysByDate = (daysNumber: number, date: Date | '') => moment(date).add(daysNumber, 'days').toDate();

export const insightsDateFormat = (date: Date) => dateFormat(date, WORDS_MONTHS_FORMAT);
export const insightsDateHourFormat = (date: Date) => dateFormat(date, WORDS_MONTHS_HOURS_FORMAT);
export const insightsShortDateFormat = (date: Date) => dateFormat(date, WORDS_MONTHS_SHORT_FORMAT);
export const insightsHourFormat = (date: Date) => dateFormat(date, HOUR_FORMAT);

export const isValidHour = (hour: string) => {
    const timeFormatRegex = /^([0-1]\d|2[0-3]):[0-5]\d$/;
    return timeFormatRegex.test(hour);
};

export const getToFromDatesLastXMinutesToNow = (minutes: number): [Date, Date] =>
    [moment().utc().subtract(minutes, 'minutes').toDate(), moment().utc().toDate()];

export const getToFromDatesLastXHoursToNow = (hours: number): [Date, Date] =>
    [moment().utc().subtract(hours, 'hours').toDate(), moment().utc().toDate()];

const getToFromDatesLastXDaysToNow = (days: number): [Date, Date] => [
    moment().utc().subtract(days, 'days').toDate(),
    moment().utc().toDate(),
];

const timeRangeToDates: Record<string, () => [Date, Date]> = {
    [TimeRange.LAST_5_MINS]: () => getToFromDatesLastXMinutesToNow(5),
    [TimeRange.LAST_10_MINS]: () => getToFromDatesLastXMinutesToNow(10),
    [TimeRange.LAST_30_MINS]: () => getToFromDatesLastXMinutesToNow(30),
    [TimeRange.LAST_60_MINS]: () => getToFromDatesLastXMinutesToNow(60),
    [TimeRange.LAST_4_HOURS]: () => getToFromDatesLastXHoursToNow(4),
    [TimeRange.LAST_12_HOURS]: () => getToFromDatesLastXHoursToNow(12),
    [TimeRange.TODAY]: () => [moment().utc().startOf('day').toDate(), moment().utc().toDate()],
    [TimeRange.YESTERDAY]: () => [
        moment().utc().subtract(1, 'days').startOf('day').toDate(),
        moment().utc().subtract(1, 'days').endOf('day').toDate(),
    ],
    [TimeRange.LAST_24_HOURS]: () => getToFromDatesLastXHoursToNow(24),
    [TimeRange.LAST_7_DAYS]: () => getToFromDatesLastXDaysToNow(7),
};

export const getStartAndEndDate = (timeRange: string): [Date, Date] => {
    const defaultRange = () => getToFromDatesLastXDaysToNow(7);
    const dateFn = timeRangeToDates[timeRange] || defaultRange;
    return dateFn();
};

export const getTimeUnit = (data: TimeEntity[]) => {
    const firstDate = moment(data[0].time);
    const lastDate = moment(data.at(-1)!.time);
    const diffDays = lastDate.diff(firstDate, 'days');
    let unit;
    if (diffDays <= 1) {
        unit = 'hour';
    } else if (diffDays < 30) {
        unit = 'day';
    } else {
        unit = 'week';
    }
    return unit;
};

export const convertDateRange = (dateRange: DateRange): DateRange =>
    typeof dateRange === 'string' ? dateRange : { from: new Date(dateRange.from), to: new Date(dateRange.to) };

export const handleDateRange = (dateRange: DateRange) => {
    let fromDate, toDate;

    if (typeof dateRange === 'string') {
        const duration = DATE_OPTIONS_MAP[dateRange];
        fromDate = moment().subtract(duration.value, duration.unit).valueOf();
        toDate = moment().valueOf();
    } else {
        fromDate = moment(dateRange.from).startOf('day').valueOf();
        toDate = moment(dateRange.to).endOf('day').valueOf();
        if (toDate > moment().valueOf()) {
            toDate = moment().valueOf();
        }
    }

    return { fromDate, toDate };
};

export const getInitializedDateTimeForPolicy = (): Date => {
    const roundedHour = new Date();
    roundedHour.setMinutes(0, 0, 0);
    roundedHour.setHours(roundedHour.getHours() + 2);
    return roundedHour;
};

export const formatCommonDateTime = (date?: Date) => {
    if (!date) return '';
    return moment(date).format(COMMON_DATE_TIME_FORMAT);
};

export const formatMonthInSort = (date?: Date) => {
    if (!date) return '';
    return moment(date).format(WORDS_MONTHS_FORMAT);
};

export const formatAppliedDate = (date?: string): string => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

export const sortByDate = (order: 'asc' | 'desc', dateA?: Date, dateB?: Date): number => {

    if(!dateA || !dateB) return 0;

    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    return order === 'asc' ? timeA - timeB : timeB - timeA;
};

export function getTrendDateLabel(fromDate: number, toDate: number): string {
    const margin = 60 * 1000; // 1 minute
    if (Math.abs(toDate - Date.now()) < margin) {
        const match = Object.entries(DATE_OPTIONS_MAP).find(([, opt]) =>
            Math.abs(fromDate - moment(toDate).subtract(opt.value, opt.unit).valueOf()) < margin
        );
        if (match) return match[0];
    }
    return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
}
